import { useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { ScanLine } from 'lucide-react';

/**
 * Lightweight camera-based QR scanner dialog. On a successful scan the
 * decoded text is normalised through `extract` (default: pulls the trailing
 * QR-NNN code out of either a raw `QR-051` string or a full feedback URL
 * like `https://.../feedback/card/qr-051`) and handed back via `onScan`.
 *
 * The dialog tears the camera down on close so we don't leak the stream.
 */
const DEFAULT_EXTRACT = (text) => {
  if (!text) return '';
  const raw = String(text).trim();
  // Full URL → grab the last path segment
  if (/\/feedback\/card\//i.test(raw)) {
    const m = raw.match(/\/feedback\/card\/([^/?#]+)/i);
    if (m) return decodeURIComponent(m[1]).toUpperCase();
  }
  return raw.toUpperCase();
};

export default function QRScannerDialog({ open, onClose, onScan, extract = DEFAULT_EXTRACT }) {
  const scannerRef = useRef(null);
  const containerId = 'qr-scanner-region';
  const handledRef = useRef(false);
  // Keep latest callbacks in refs so parent re-renders never restart the camera.
  const onScanRef = useRef(onScan);
  const extractRef = useRef(extract);
  onScanRef.current = onScan;
  extractRef.current = extract;

  // html5-qrcode's stop() THROWS synchronously if the scanner isn't running,
  // so every stop goes through this guard (never a raw inst.stop()).
  const stopScanner = (s) => {
    if (!s) return Promise.resolve();
    let p;
    try {
      p = s.isScanning ? s.stop() : Promise.resolve();
    } catch {
      p = Promise.resolve();
    }
    return Promise.resolve(p).catch(() => {}).finally(() => {
      try { s.clear(); } catch { /* noop */ }
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    handledRef.current = false;
    let cancelled = false;
    let timer = null;
    let tries = 0;

    // The dialog content mounts via a portal with an open animation, so the
    // container div may not exist yet on the first render — poll until it does.
    const boot = () => {
      if (cancelled) return;
      if (!document.getElementById(containerId)) {
        tries += 1;
        if (tries > 40) { onScanRef.current(null); return; }
        timer = setTimeout(boot, 100);
        return;
      }
      let inst;
      try {
        inst = new Html5Qrcode(containerId, { verbose: false });
      } catch {
        timer = setTimeout(boot, 100);
        return;
      }
      scannerRef.current = inst;
      inst
        .start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            if (handledRef.current) return;
            handledRef.current = true;
            const code = extractRef.current(decoded);
            // Detach BEFORE stopping so the effect cleanup can't double-stop.
            scannerRef.current = null;
            stopScanner(inst).finally(() => onScanRef.current(code));
          },
          () => { /* scan errors per-frame — ignore */ },
        )
        .catch(() => {
          // Camera unavailable / permission denied — surface a soft hint.
          if (!cancelled) onScanRef.current(null);
        });
    };
    timer = setTimeout(boot, 50);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      const s = scannerRef.current;
      scannerRef.current = null;
      stopScanner(s);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="qr-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-primary" />
            Scan QR Card
          </DialogTitle>
          <DialogDescription>
            Point the rear camera at the QR on the physical card.
          </DialogDescription>
        </DialogHeader>
        <div id={containerId} className="w-full aspect-square bg-black rounded-sm overflow-hidden" />
        <p className="text-[11px] text-muted-foreground text-center">
          Tip: hold the card 10–20 cm from the camera in good light.
        </p>
      </DialogContent>
    </Dialog>
  );
}
